import { PAYMENT_METHODS } from '@monorepo/shared/schemas/common'
import { PAYMENT_STATUSES } from '@monorepo/shared/schemas/payments'
import { FilterIcon, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

export interface PaymentFilters {
  filter_field?: 'customer_name' | 'payment_id' | 'organization_name'
  filter_operator?: 'is' | 'starts_with' | 'ends_with' | 'contains'
  filter_value?: string
  payment_status?: string[]
  payment_method?: string[]
}

interface FilterOption {
  value: string
  label: string
}

interface PaymentFiltersProps {
  filters: PaymentFilters
  onFiltersChange: (filters: PaymentFilters) => void
  onSearch: () => void
  onReset: () => void
}

const paymentStatusOptions: FilterOption[] = PAYMENT_STATUSES.map(s => ({
  value: s,
  label:
    s === 'succeeded'
      ? 'Completed'
      : s === 'canceled'
        ? 'Canceled'
        : s.charAt(0).toUpperCase() + s.slice(1),
}))

const paymentMethodOptions: FilterOption[] = PAYMENT_METHODS.map(m => ({
  value: m,
  label: m
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' '),
}))

const filterConfig = {
  fields: [
    { value: 'customer_name', label: 'Customer Name' },
    { value: 'payment_id', label: 'Payment ID' },
    { value: 'organization_name', label: 'Organization' },
  ] as FilterOption[],
  operators: [
    { value: 'is', label: 'Is' },
    { value: 'starts_with', label: 'Starts with' },
    { value: 'ends_with', label: 'Ends with' },
    { value: 'contains', label: 'Contains' },
  ] as FilterOption[],
  paymentStatuses: paymentStatusOptions,
  paymentMethods: paymentMethodOptions,
}

export function PaymentFilters({
  filters,
  onFiltersChange,
  onSearch: _onSearch,
  onReset,
}: PaymentFiltersProps) {
  const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState<
    string[]
  >(filters.payment_status || [])
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<
    string[]
  >(filters.payment_method || [])
  const [inputValue, setInputValue] = useState(filters.filter_value || '')
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync local state when filters reset externally
  useEffect(() => {
    setSelectedPaymentStatuses(filters.payment_status || [])
    setSelectedPaymentMethods(filters.payment_method || [])
    setInputValue(filters.filter_value || '')
  }, [filters.payment_status, filters.payment_method, filters.filter_value])

  const updateFilter = useCallback(
    (key: keyof PaymentFilters, value: string | string[] | undefined) => {
      onFiltersChange({
        ...filters,
        [key]: value,
      })
    },
    [filters, onFiltersChange]
  )

  const handleValueInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)
    if (debounceTimer.current) clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      updateFilter('filter_value', val || undefined)
    }, 500)
  }

  const handlePaymentStatusToggle = (statusValue: string, checked: boolean) => {
    const newStatuses = checked
      ? [...selectedPaymentStatuses, statusValue]
      : selectedPaymentStatuses.filter(s => s !== statusValue)

    setSelectedPaymentStatuses(newStatuses)
    updateFilter('payment_status', newStatuses.length ? newStatuses : undefined)
  }

  const handlePaymentMethodToggle = (methodValue: string, checked: boolean) => {
    const newMethods = checked
      ? [...selectedPaymentMethods, methodValue]
      : selectedPaymentMethods.filter(m => m !== methodValue)

    setSelectedPaymentMethods(newMethods)
    updateFilter('payment_method', newMethods.length ? newMethods : undefined)
  }

  const clearFilter = (key: keyof PaymentFilters) => {
    if (key === 'payment_status') {
      setSelectedPaymentStatuses([])
    } else if (key === 'payment_method') {
      setSelectedPaymentMethods([])
    } else if (key === 'filter_value') {
      setInputValue('')
    }
    updateFilter(key, undefined)
  }

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'page' || key === 'limit' || key === 'sort' || key === 'order')
      return false
    return (
      value !== undefined &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    )
  })

  return (
    <div className='mb-6'>
      {/* Single Row Filter - No Card Background */}
      <div className='flex gap-3 items-end justify-between'>
        <div className='flex gap-3 items-center'>
          {/* Field Select */}
          <div className='w-40'>
            <Select
              value={filters.filter_field || ''}
              onValueChange={value =>
                updateFilter(
                  'filter_field',
                  value as PaymentFilters['filter_field']
                )
              }
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Select field' />
              </SelectTrigger>
              <SelectContent>
                {filterConfig.fields.map(field => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operator Select */}
          <div className='w-28'>
            <Select
              value={filters.filter_operator || ''}
              onValueChange={value =>
                updateFilter(
                  'filter_operator',
                  value as PaymentFilters['filter_operator']
                )
              }
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Operator' />
              </SelectTrigger>
              <SelectContent>
                {filterConfig.operators.map(operator => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value Input — 500ms debounce */}
          <div className='w-80'>
            <Input
              placeholder='Search value'
              value={inputValue}
              onChange={handleValueInputChange}
              className='h-8 text-xs'
            />
          </div>
        </div>

        {/* Right Side Filters */}
        <div className='flex gap-3 items-center'>
          {/* Payment Status Filter */}
          <div className='w-48'>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full h-8 text-xs justify-start'
                >
                  <FilterIcon className='w-3 h-3 mr-1' />
                  Payment Status ({selectedPaymentStatuses.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-48' align='start'>
                <div className='space-y-2'>
                  <div className='font-medium text-sm'>
                    Select Payment Status
                  </div>
                  {filterConfig.paymentStatuses.map(status => (
                    <div
                      key={status.value}
                      className='flex items-center space-x-2'
                    >
                      <Checkbox
                        id={`payment-status-${status.value}`}
                        checked={selectedPaymentStatuses.includes(status.value)}
                        onCheckedChange={checked =>
                          handlePaymentStatusToggle(
                            status.value,
                            checked as boolean
                          )
                        }
                      />
                      <Label
                        htmlFor={`payment-status-${status.value}`}
                        className='text-sm'
                      >
                        {status.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Payment Method Filter */}
          <div className='w-48'>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  className='w-full h-8 text-xs justify-start'
                >
                  <FilterIcon className='w-3 h-3 mr-1' />
                  Payment Method ({selectedPaymentMethods.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-56' align='start'>
                <div className='space-y-2'>
                  <div className='font-medium text-sm'>
                    Select Payment Method
                  </div>
                  {filterConfig.paymentMethods.map(method => (
                    <div
                      key={method.value}
                      className='flex items-center space-x-2'
                    >
                      <Checkbox
                        id={`payment-method-${method.value}`}
                        checked={selectedPaymentMethods.includes(method.value)}
                        onCheckedChange={checked =>
                          handlePaymentMethodToggle(
                            method.value,
                            checked as boolean
                          )
                        }
                      />
                      <Label
                        htmlFor={`payment-method-${method.value}`}
                        className='text-sm'
                      >
                        {method.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reset Button */}
          {hasActiveFilters && (
            <Button
              variant='outline'
              size='sm'
              onClick={onReset}
              className='h-8'
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className='flex gap-2 flex-wrap mt-3'>
          <span className='text-sm text-muted-foreground'>Active filters:</span>

          {filters.filter_field &&
            filters.filter_operator &&
            filters.filter_value && (
              <Badge variant='secondary' className='gap-1'>
                {filters.filter_field} {filters.filter_operator} &quot;
                {filters.filter_value}&quot;
                <X
                  className='w-3 h-3 cursor-pointer'
                  onClick={() => {
                    clearFilter('filter_field')
                    clearFilter('filter_operator')
                    clearFilter('filter_value')
                  }}
                />
              </Badge>
            )}

          {selectedPaymentStatuses.length > 0 && (
            <Badge variant='secondary' className='gap-1'>
              Payment Status: {selectedPaymentStatuses.join(', ')}
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => clearFilter('payment_status')}
              />
            </Badge>
          )}

          {selectedPaymentMethods.length > 0 && (
            <Badge variant='secondary' className='gap-1'>
              Payment Method: {selectedPaymentMethods.join(', ')}
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => clearFilter('payment_method')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}
